import { accountError, accountRequest, accountSetStorage, accountStore, accountUpdateNotifs, notificationsRequest } from '@store/slices/account/accountSlice.js';
import { blocksUpdateArchivedCount, blocksUpdateDragList, blocksUpdateInitialList, blocksUpdateList } from '@store/slices/blocks/blocksSlice.js';
import { footerStore } from '@store/slices/blocks/footer/footerSlice.js';
import { headerStore, headerUpdateMusic, headerUpdateSliderItems } from '@store/slices/blocks/header/headerSlice.js';
import { promoStore } from '@store/slices/blocks/promo/promoSlice.js';
import { splashStore } from '@store/slices/blocks/splash/splashSlice.js';
import { welcomeStore } from '@store/slices/blocks/welcome/welcomeSlice.js';
import { designStore, designStoreId } from '@store/slices/design/designSlice.js';
import { pagesStore } from '@store/slices/pages/pagesSlice.js';
import { postsStore } from '@store/slices/posts/postsSlice.js';
import { settingsStoreSlice } from '@store/slices/settings/settingsSlice.js';
import { spotlightStore } from '@store/slices/spotlight/spotlightSlice.js';
// import { headerStore } from '@store/toolkit/blocks/header/headerActions.js';
import { setPostList, setTabsList } from '@store/toolkit/post/postActions.js';
import { settingsStore } from '@store/toolkit/settings/settingsActions.js';
import { STORAGE_KEYS } from '@utils/constants.js';
import httpClient, { source } from '@utils/httpClient';
import { getAccountMap } from '@utils/mapper';
import { checkIsDragBlock } from '@views/AdminPanelView/components/AdminPanel/components/LinkInBio/components/MyPages/utils/blocksFunctions.js';
import { call, put, takeLatest } from 'redux-saga/effects';

function getAccountApiCall() {
  return httpClient.get(`/api/users`, { cancelToken: source.token });
  // return httpClient.get(`/api/users2`, { cancelToken: source.token });
}

function* getAccountDataSaga({ payload }) {
  try {
    const { profileId } = payload;

    const { data } = yield call(getAccountApiCall);

    // Server returned empty data — treat as error so the UI shows the support popup
    if (!data.profile && !data.user) {
      yield put(accountError({ message: 'Account data is unavailable. Please contact support.', status: null }));
      return;
    }

    localStorage.setItem(STORAGE_KEYS.unilinkProfileId, data?.profile?.id);

    const notifs = data.board?.notifs;
    yield put(accountUpdateNotifs(notifs));

    yield put(spotlightStore(data?.profile?.spotlight));

    const { blocks, design, settings, posts, postsTiers, tabs, pages, homePageId, isDisplayPreviewPosts, archivedBlocksCount } = data.profile;
    yield put(blocksUpdateArchivedCount(archivedBlocksCount ?? null));
    yield put(designStore(design));
    yield put(designStoreId(design));
    yield put(settingsStore(settings));
    yield put(settingsStoreSlice(settings));
    yield put(setPostList(posts ?? []));
    yield put(setTabsList(tabs ?? []));

    // Fetch slider items for header layout 5 so preview shows dots immediately
    const headerBlock = blocks.find((el) => el.type === 'header');
    let headerSliderItems = [];
    if (headerBlock?.data?.blockContent?.layout === 5 && headerBlock.id) {
      try {
        const sliderRes = yield call(() => httpClient.get(`api/header-blocks/${headerBlock.id}/profilepic/slider`));
        headerSliderItems = sliderRes.data ?? [];
      } catch (e) {
        // slider items will remain empty
      }
    }

    // Inject profile music and slider items into header blockContent so preview can display them
    const music = data.profile.music;
    const hasMusic = music && (music.url || music.id);
    const hasSliderItems = headerSliderItems.length > 0;

    const processedBlocks =
      hasMusic || hasSliderItems
        ? blocks.map((block) => {
            if (block.type === 'header') {
              const blockContent = { ...block.data.blockContent };
              if (hasMusic) {
                blockContent.music = {
                  url: music.url || '',
                  title: music.filename || '',
                  startVolume: music.volume ?? 40,
                  id: music.id || '',
                };
              }
              if (hasSliderItems) {
                blockContent.sliderItems = headerSliderItems;
              }
              return { ...block, data: { ...block.data, blockContent } };
            }
            return block;
          })
        : blocks;

    yield put(blocksUpdateList(processedBlocks));
    yield put(blocksUpdateInitialList(processedBlocks));

    const dragList = blocks.filter((el) => checkIsDragBlock(el)).sort((prev, next) => (prev.n > next.n ? 1 : -1));
    yield put(blocksUpdateDragList(dragList));

    const splashSceenData = blocks.find((el) => el.type === 'splash');
    if (splashSceenData) {
      yield put(splashStore(splashSceenData));
    }

    const promoData = blocks.find((el) => el.type === 'promo');
    if (promoData) {
      console.log('promoData', promoData);
      yield put(promoStore(promoData));
    }

    if (headerBlock) {
      yield put(headerStore(headerBlock));
      if (hasSliderItems) {
        yield put(headerUpdateSliderItems(headerSliderItems));
      }
    }

    if (music && (music.url || music.id)) {
      yield put(
        headerUpdateMusic({
          url: music.url || '',
          title: music.filename || '',
          startVolume: music.volume ?? 40,
          id: music.id || '',
        }),
      );
    }

    const welcomeData = blocks.find((el) => el.type === 'welcome');
    if (welcomeData) {
      yield put(welcomeStore(welcomeData));
    }

    const footerData = blocks.find((el) => el.type === 'footer');
    if (footerData) {
      yield put(footerStore(footerData));
    }

    yield put(accountStore(getAccountMap(data)));
    yield put(notificationsRequest());
    yield put(pagesStore({ list: pages, homePageId }));
    yield put(postsStore({ list: posts, isDisplayPreviewPosts, tiersList: postsTiers }));

    if (data?.profile?.id) {
      yield put(accountSetStorage(data?.profile?.id));
    }
  } catch (error) {
    console.log('getAccountDataSaga error', error);
    // Carry the status through: an expired session and a failure inside api/users both end up here,
    // and only the first one is fixed by signing in again.
    yield put(
      accountError({
        message: error.message ? error.message : error.toString(),
        status: error.response?.status ?? null,
      }),
    );
  }
}

export function* getAccountDataWatcher() {
  yield takeLatest(accountRequest, getAccountDataSaga);
}
